/**
 * Settings · Backup E2E — exports ALL the local state (identity + ratchets +
 * conversations + messages) encrypted with a passphrase only the
 * user knows, and imports/restores a backup in this browser.
 *
 * Zero-knowledge: the blob can be stored anywhere without revealing anything;
 * it is never uploaded automatically. Exporting is a Plus plan capability (flag-based
 * monetization framework, NO ads); RESTORING is always allowed.
 */

import { createSignal, Show } from "solid-js";
import { createRoute, useNavigate } from "@tanstack/solid-router";
import { ArrowLeft, Download, ShieldCheck, Sparkles, Upload, Vault } from "@fleet/icons";
import { msg } from "@lingui/core/macro";

import { rootRoute } from "@/routes/__root";
import { useChat } from "@/providers/chat-provider";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@/components/ui/glass-surface";
import { Atmosphere } from "@/components/ui/atmosphere";
import { importEncryptedBackup } from "@/lib/chat/backup";
import { adoptRestoredState } from "@/lib/chat/persistence";
import { canUseBackup, getPlan, setPlan } from "@/lib/monetization/entitlements";

function SettingsBackupComponent() {
  const chat = useChat();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [plan, setPlanSignal] = createSignal(getPlan());
  const [exportPass, setExportPass] = createSignal("");
  const [exportOut, setExportOut] = createSignal("");
  const [exportError, setExportError] = createSignal(false);
  const [importText, setImportText] = createSignal("");
  const [importPass, setImportPass] = createSignal("");
  const [importError, setImportError] = createSignal(false);
  const [busy, setBusy] = createSignal(false);

  const backupAllowed = () => canUseBackup(plan());

  const activatePlus = () => {
    // Flag-based monetization framework: when a real gateway exists (manual
    // action of the owner), its license will write this same flag. NO ads.
    setPlan("plus");
    setPlanSignal("plus");
  };

  const doExport = async () => {
    setExportError(false);
    setExportOut("");
    setBusy(true);
    try {
      setExportOut(await chat.exportBackup(exportPass()));
    } catch {
      setExportError(true);
    } finally {
      setBusy(false);
    }
  };

  const downloadBackup = () => {
    const blob = new Blob([exportOut()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "susurro-backup.json";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const doRestore = async () => {
    setImportError(false);
    setBusy(true);
    try {
      const state = await importEncryptedBackup(importText().trim(), importPass());
      adoptRestoredState(state);
      // Reload: the next boot starts with the restored identity.
      location.reload();
    } catch {
      setImportError(true);
      setBusy(false);
    }
  };

  const fieldClass =
    "rounded-xl border border-[var(--field-border)] bg-[var(--field-background)] px-3.5 py-2.5 text-sm text-foreground placeholder:text-[var(--field-placeholder)] outline-none transition-shadow focus:ring-2 focus:ring-ring/60";

  return (
    <div class="relative min-h-dvh w-full">
      <Atmosphere />
      <div class="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-5 p-4 sm:p-6">
        <header class="flex items-center gap-3 pt-6 pb-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t(msg`Back`)}
            onClick={() => {
              void navigate({ to: "/" });
            }}
          >
            <ArrowLeft class="h-5 w-5" />
          </Button>
          <div class="flex items-center gap-2">
            <Vault class="h-5 w-5 text-primary" />
            <h1 class="text-lg font-semibold tracking-tight">{t(msg`Encrypted backup`)}</h1>
          </div>
          <span
            data-testid="plan-badge"
            data-plan={plan()}
            class="ml-auto rounded-full bg-primary/12 px-2.5 py-1 text-[11px] font-semibold tracking-wide text-primary uppercase"
          >
            <Show when={plan() === "plus"} fallback={t(msg`Free plan`)}>
              {t(msg`Plus plan`)}
            </Show>
          </span>
        </header>

        <p class="text-xs text-muted-foreground">
          {t(
            msg`Your chats live only on this device. A backup encrypts everything with a passphrase only you know — you can store the file anywhere without revealing a thing. Nothing is ever uploaded automatically.`,
          )}
        </p>

        <Show when={!backupAllowed()}>
          <GlassSurface class="flex flex-col gap-3 p-4" data-testid="backup-gate">
            <div class="flex items-center gap-2 text-sm font-semibold">
              <Sparkles class="h-4 w-4 text-primary" />
              {t(msg`Encrypted backup is a Plus capability`)}
            </div>
            <p class="text-xs text-muted-foreground">
              {t(
                msg`Susurro is free for private 1:1 chats and groups — with no ads, ever. Plus adds large groups, heavy media and encrypted backups. Restoring a backup is always allowed.`,
              )}
            </p>
            <Button class="gap-2" data-testid="activate-plus" onClick={activatePlus}>
              <Sparkles class="h-4 w-4" />
              {t(msg`Activate Plus on this device`)}
            </Button>
          </GlassSurface>
        </Show>

        <Show when={backupAllowed()}>
          <GlassSurface class="flex flex-col gap-3 p-4">
            <div class="flex items-center gap-2 text-sm font-semibold">
              <Download class="h-4 w-4 text-primary" />
              {t(msg`Export a backup`)}
            </div>
            <input
              data-testid="backup-passphrase"
              type="password"
              class={fieldClass}
              placeholder={t(msg`Backup passphrase (min. 6 characters)`)}
              value={exportPass()}
              onInput={(e) => setExportPass(e.currentTarget.value)}
            />
            <Button
              class="gap-2"
              data-testid="backup-export"
              disabled={busy() || exportPass().length < 6}
              onClick={() => void doExport()}
            >
              <ShieldCheck class="h-4 w-4" />
              {t(msg`Encrypt and export`)}
            </Button>
            <Show when={exportError()}>
              <p role="alert" class="text-xs text-destructive" data-testid="backup-export-error">
                {t(msg`Could not create the backup.`)}
              </p>
            </Show>
            <Show when={exportOut()}>
              <textarea
                data-testid="backup-output"
                readOnly
                rows={4}
                class={`${fieldClass} font-mono text-[11px] break-all`}
                value={exportOut()}
              />
              <Button
                variant="outline"
                class="gap-2"
                data-testid="backup-download"
                onClick={downloadBackup}
              >
                <Download class="h-4 w-4" />
                {t(msg`Download file`)}
              </Button>
            </Show>
          </GlassSurface>
        </Show>

        <GlassSurface class="flex flex-col gap-3 p-4">
          <div class="flex items-center gap-2 text-sm font-semibold">
            <Upload class="h-4 w-4 text-primary" />
            {t(msg`Restore a backup`)}
          </div>
          <p class="text-xs text-muted-foreground">
            {t(
              msg`Paste the backup content and enter its passphrase. This browser will adopt the backed-up identity and chats after reloading.`,
            )}
          </p>
          <textarea
            data-testid="restore-input"
            rows={4}
            class={`${fieldClass} font-mono text-[11px] break-all`}
            placeholder={t(msg`Backup content`)}
            value={importText()}
            onInput={(e) => setImportText(e.currentTarget.value)}
          />
          <input
            data-testid="restore-passphrase"
            type="password"
            class={fieldClass}
            placeholder={t(msg`Backup passphrase`)}
            value={importPass()}
            onInput={(e) => setImportPass(e.currentTarget.value)}
          />
          <Button
            class="gap-2"
            data-testid="backup-restore"
            disabled={busy() || importText().trim().length === 0 || importPass().length === 0}
            onClick={() => void doRestore()}
          >
            <Upload class="h-4 w-4" />
            {t(msg`Restore`)}
          </Button>
          <Show when={importError()}>
            <p role="alert" class="text-xs text-destructive" data-testid="restore-error">
              {t(msg`Could not restore: wrong passphrase or invalid backup.`)}
            </p>
          </Show>
        </GlassSurface>
      </div>
    </div>
  );
}

export const settingsBackupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/backup",
  component: SettingsBackupComponent,
});
